'use client';

import { useContext, useEffect, useState, type FC } from 'react';
import { Heading, Tooltip, Flex, Icon, Text } from '@/_components';
import type { DocumentsProps } from './Documents.types';
import type { Document as DocumentType } from '@/_types/documents';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { UserContext } from '@/_contexts/UserProvider';
import { getExportedDocumentsList } from '@/_api/client-questionnaires';
import { SortableDocumentItem } from './components/SortableDocumentItem';

export const Documents: FC<DocumentsProps> = ({
  documents,
  companyId,
  onToggleDocument,
  onDescriptionUpdated,
  onDragEnd,
}) => {
  const { token } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [exportedDocuments, setExportedDocuments] = useState<DocumentType[]>(
    [],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = documents.findIndex((doc) => doc.id === active.id);
      const newIndex = documents.findIndex((doc) => doc.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onDragEnd(oldIndex, newIndex);
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getExportedDocumentsList({ id: companyId, token }).then((data) => {
      setExportedDocuments(
        (data as (DocumentType & { document: string })[]).map((doc) => {
          const { document, ...rest } = doc;
          return {
            ...rest,
            source: document,
          };
        }),
      );
      setIsLoading(false);
    });
  }, [companyId, token]);

  if (isLoading) return null;

  return (
    <>
      <Flex gap={2} isBetween isYCentered>
        <Heading level={5}>Documents</Heading>
        <Tooltip placement="bottom">
          <Tooltip.Trigger>
            <Flex gap={2}>
              <Icon name="info" size={16} color="secondary" />
              <Text type="small" color="secondary">
                What will be included in the PDF export?
              </Text>
            </Flex>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <Text type="small" color="currentcolor">
              PDFs, Excel files (.xlsx, .xls, .xlsm), and most image formats
              will be shown in the PDF export. All other formats will be linked.
            </Text>
          </Tooltip.Content>
        </Tooltip>
      </Flex>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={documents.map((doc) => doc.id)}
          strategy={verticalListSortingStrategy}
        >
          <Flex isColumn={true} gap={4}>
            {documents.map((document) => {
              const documentName = document.source.split('/').pop() ?? '';

              return (
                <SortableDocumentItem
                  key={document.id}
                  document={document}
                  documentName={documentName}
                  companyId={companyId}
                  exportedDocuments={exportedDocuments}
                  onToggleDocument={onToggleDocument}
                  onDescriptionUpdated={onDescriptionUpdated}
                />
              );
            })}
          </Flex>
        </SortableContext>
      </DndContext>
    </>
  );
};
